import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PERMISSIONS } from '@lims/permissions';
import { settingsApi, type TenantSettings } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  Panel,
  ReadOnlyField,
  SaveErrorNotice,
  SwitchField,
  TextAreaField,
  useSaveErrors,
  useSaveFailureFocus,
  useServerForm,
  useToast,
} from '../../components/ui/index.js';

/** The shared schema's limit on each half of the footer. */
const MAXIMUM_FOOTER = 1000;

/** The form, as the server holds it. */
function fromServer(source: TenantSettings) {
  return {
    footerAr: source.reportFooterText?.ar ?? '',
    footerEn: source.reportFooterText?.en ?? '',
    // Only an explicit `true` from the server turns it on.
    downloadEnabled: source.verificationDownloadEnabled === true,
  };
}

/**
 * Report settings — Phase 10, decisions D111 and D126.
 *
 * TWO things, both the laboratory's own and neither restated by this page:
 *
 *  - The footer wording printed on every page of a report. EMPTY by default:
 *    no wording this product could supply belongs to any laboratory
 *    (CLAUDE.md section 6.1). It applies to reports issued from now on; an
 *    issued report keeps the footer it was issued with.
 *
 *  - Whether a holder of a report's verification link may download the
 *    original from the public page. Its value comes from the server — the
 *    backend's default for a new laboratory is OFF (D111), and it is not
 *    repeated here. The helper text says plainly what turning it on exposes.
 *
 * Saved on its own, apart from the main settings form, so this consequential
 * switch never rides along with an unrelated edit. What the page shows after
 * saving is what the SERVER returned, not what was sent (CLAUDE.md section 8).
 */
export function ReportSettingsPanel({ settings }: { readonly settings: TenantSettings }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const { show } = useToast();
  const queryClient = useQueryClient();

  const canEdit = can(PERMISSIONS.SETTINGS_UPDATE);

  // Authoritative while edited (D205): a save elsewhere on the page, or a
  // refetch, never replaces an unsaved footer; only this panel's own
  // successful save resets it.
  const draft = useServerForm(settings, fromServer, fromServer(settings));
  const form = draft.values;

  const save = useMutation({
    mutationFn: ({ values }: { values: typeof form; mark: number }) =>
      settingsApi.update({
        reportFooterText: {
          ar: values.footerAr.trim() || null,
          en: values.footerEn.trim() || null,
        },
        verificationDownloadEnabled: values.downloadEnabled,
      }),
    onSuccess: async (saved, { mark }) => {
      draft.saved(saved, mark);
      queryClient.setQueryData(['settings'], saved);
      show({ tone: 'success', message: t('settings.savedNotice') });
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
  });
  const { fields: serverErrors } = useSaveErrors(save.error);
  const panelRef = useRef<HTMLDivElement>(null);
  const noticeRef = useRef<HTMLDivElement>(null);
  useSaveFailureFocus(save.error, panelRef, noticeRef);
  const footerError = (half: 'ar' | 'en') =>
    serverErrors[`reportFooterText.${half}`] ?? serverErrors.reportFooterText;

  const tooLongAr = form.footerAr.trim().length > MAXIMUM_FOOTER;
  const tooLongEn = form.footerEn.trim().length > MAXIMUM_FOOTER;

  return (
    <Panel title={t('settings.reports.title')} description={t('settings.reports.description')}>
      <div ref={panelRef} className="flex flex-col gap-gutter">
        <div className="flex flex-col gap-inline">
          {canEdit ? (
            <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
              {/* Each half in its own content direction; never auto-translated. */}
              <div dir="rtl">
                <TextAreaField
                  label={t('settings.reports.footerTextAr')}
                  value={form.footerAr}
                  onChange={(value) => draft.set('footerAr', value)}
                  rows={3}
                  {...(tooLongAr
                    ? { error: t('validation.tooLong') }
                    : footerError('ar')
                      ? { error: footerError('ar') }
                      : {})}
                />
              </div>
              <div dir="ltr">
                <TextAreaField
                  label={t('settings.reports.footerTextEn')}
                  value={form.footerEn}
                  onChange={(value) => draft.set('footerEn', value)}
                  rows={3}
                  {...(tooLongEn
                    ? { error: t('validation.tooLong') }
                    : footerError('en')
                      ? { error: footerError('en') }
                      : {})}
                />
              </div>
            </div>
          ) : (
            /*
              Plain text for a reader who may not change it — not a field that
              only looks editable (the text-area wrapper has no read-only mode).
            */
            <div className="grid grid-cols-1 gap-stack sm:grid-cols-2">
              <div dir="rtl">
                <ReadOnlyField label={t('settings.reports.footerTextAr')}>
                  <span className="whitespace-pre-wrap">{form.footerAr || t('common.notSet')}</span>
                </ReadOnlyField>
              </div>
              <div dir="ltr">
                <ReadOnlyField label={t('settings.reports.footerTextEn')}>
                  <span className="whitespace-pre-wrap">{form.footerEn || t('common.notSet')}</span>
                </ReadOnlyField>
              </div>
            </div>
          )}
          <p className="text-caption text-default-600">{t('settings.reports.footerHint')}</p>
          <p className="text-caption text-default-600">
            {t('settings.reports.footerAppliesHint')}
          </p>
        </div>

        <div className="flex flex-col gap-stack">
          <SwitchField
            label={t('settings.reports.verificationDownload')}
            description={t('settings.reports.verificationDownloadHint')}
            value={form.downloadEnabled}
            onChange={(value) => draft.set('downloadEnabled', value)}
            isDisabled={!canEdit}
          />
          {/* What each position means, for the position the switch is in. */}
          <Alert
            tone={form.downloadEnabled ? 'warning' : 'info'}
            title={
              form.downloadEnabled
                ? t('settings.reports.verificationDownloadOnNote')
                : t('settings.reports.verificationDownloadOffNote')
            }
          />
        </div>

        {save.error ? (
          <div ref={noticeRef} tabIndex={-1} className="outline-none">
            <SaveErrorNotice error={save.error} title={t('settings.saveFailed')} />
          </div>
        ) : null}

        <ActionBar>
          {can(PERMISSIONS.TEMPLATES_VIEW) ? (
            <ActionButton priority="secondary" onPress={() => navigate('/report-templates')}>
              {t('settings.reports.templatesLink')}
            </ActionButton>
          ) : null}
          {canEdit ? (
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              isDisabled={tooLongAr || tooLongEn}
              onPress={() => save.mutate({ values: form, mark: draft.mark() })}
            >
              {t('settings.save')}
            </ActionButton>
          ) : null}
        </ActionBar>
      </div>
    </Panel>
  );
}
